// Ubicación en el repo: src/pages/PublicLanding.jsx
// Página pública institucional en la raíz del dominio (23-ago-2026, ver
// DECISIONS.md) — antes `/` caía directo bajo RequireAuth y un visitante
// sin sesión terminaba en /login sin ningún contexto de qué es esto. Pedido
// de Nicolás: página informativa (no vende servicios propios, es un
// software de agendamiento que colabora con ópticas comunales existentes),
// con un botón de acceso al sistema en la esquina superior y un listado de
// las sucursales con las que se trabaja hoy.
//
// Ruta pública, SIN sesión, fuera de RequireAuth/Layout — mismo criterio
// que /agendamiento-online. Reutiliza la Edge Function
// `public-agendamiento-config` (ya expuesta sin login) en vez de armar un
// endpoint nuevo: ya trae nombre/dirección/teléfono/logo/color de marca por
// sucursal, y desde la migración 0041 también `serviciosDescripcion`.
//
// Contenido de placeholder a propósito (pedido explícito de Nicolás,
// 23-ago-2026): "arma algo, lo vamos modificando en el transcurso de la
// semana" — texto institucional razonable, sin inventar datos concretos de
// las ópticas (eso sale de `serviciosDescripcion`, que Nicolás va a ir
// completando desde Configuración → Sucursales).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublicConfig } from '../lib/publicScheduling';
import { applyClinicSettings, DEFAULT_SETTINGS } from '../lib/clinicSettings';

// Nombres guardados como "OPTICA COMUNAL <lugar>" (convención propia de
// branches.name, siempre en mayúsculas) — partido en dos líneas para
// aprovechar mejor el espacio de la tarjeta: "Óptica Comunal" como
// etiqueta chica arriba, el lugar (ej. "Las Condes") destacado abajo.
// Nicolás notó que sin esto, "LAS CONDES" se cortaba en 3 líneas mientras
// nombres más cortos como "ÑUÑOA" quedaban en una sola, dando tarjetas
// visualmente inconsistentes entre sí (23-ago-2026).
//
// Si el nombre no sigue ese prefijo (ej. otra empresa usando el software
// más adelante, con su propia convención de nombres), se devuelve null y
// la tarjeta cae al comportamiento de siempre: el nombre completo en una
// sola línea — no se rompe nada para nombres que no calzan con el patrón.
const BRAND_PREFIX = 'OPTICA COMUNAL';
function splitBranchName(name) {
  const clean = (name || '').trim();
  const normalized = clean.toUpperCase().replace('Ó', 'O');
  if (!normalized.startsWith(BRAND_PREFIX)) return null;
  const place = clean.slice(BRAND_PREFIX.length).trim();
  if (!place) return null;
  return { brand: 'ÓPTICA COMUNAL', place };
}

function BranchCard({ branch }) {
  // Fallback genérico si la sucursal todavía no tiene su propio texto de
  // servicios cargado (servicios_descripcion, migración 0041) — mejor
  // mostrar algo basado en los módulos habilitados que dejar la tarjeta
  // vacía.
  const fallbackServicios = [
    branch.orlEnabled && 'Audiología',
    branch.oftEnabled && 'Óptica',
  ].filter(Boolean).join(' · ');

  const split = splitBranchName(branch.name);

  // Tarjeta con acento lateral + logo junto al nombre (30-sep-2026,
  // DECISIONS.md #126) — reemplaza el layout centrado anterior, tomando el
  // estilo de opticacomunallascondes.cl (referencia visual de Nicolás).
  return (
    <div className="landing-branch-card">
      <div className="landing-branch-card__top">
        {branch.logoUrl ? (
          <img src={branch.logoUrl} alt={`Logo de ${branch.name}`} className="landing-branch-card__logo" />
        ) : (
          <div className="landing-branch-card__logo landing-branch-card__logo--placeholder" aria-hidden="true">
            🏥
          </div>
        )}
        {split ? (
          <h3 className="landing-branch-card__name">
            <span className="landing-branch-card__name-brand">{split.brand}</span>
            <span className="landing-branch-card__name-place">{split.place}</span>
          </h3>
        ) : (
          <h3 className="landing-branch-card__name">{branch.name}</h3>
        )}
      </div>
      <p className="landing-branch-card__services">
        {branch.serviciosDescripcion?.trim() || fallbackServicios || 'Atención en convenio con Agenda Comunal.'}
      </p>
      <div className="landing-branch-card__meta">
        {branch.address && (
          <span className="landing-branch-card__meta-row">
            <span aria-hidden="true">📍</span> {branch.address}
          </span>
        )}
        {(branch.comuna || branch.region) && (
          <span className="landing-branch-card__meta-row landing-branch-card__meta-row--sub">
            {[branch.comuna, branch.region].filter(Boolean).join(', ')}
          </span>
        )}
        {branch.phone && (
          <span className="landing-branch-card__meta-row">
            <span aria-hidden="true">📞</span> {branch.phone}
          </span>
        )}
      </div>
    </div>
  );
}

export default function PublicLanding() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPublicConfig()
      .then((data) => {
        setConfig(data);
        applyClinicSettings({ ...DEFAULT_SETTINGS, primaryColor: data.primaryColor, darkMode: false });
      })
      .catch((err) => setError(err.message || 'No se pudo cargar la página. Intenta de nuevo más tarde.'));
  }, []);

  const branches = config?.branches ?? [];

  // Slides del panel del hero (1-oct-2026, pedido de Nicolás, ver
  // DECISIONS.md): rota entre las sucursales activas que tengan logo propio
  // cargado — las que no tienen logo se saltan. Se recalcula solo cuando
  // cambian las sucursales (useMemo), no en cada render.
  // El texto "Agendamiento 100% online" queda FIJO (pedido de Nicolás,
  // 1-oct-2026, ronda 2): solo rota el logo, no el caption.
  //
  // Fix (1-oct-2026, bug reportado por Nicolás): antes se agregaba un
  // slide fijo de "marca" con /logo-optica-comunal.png ADEMÁS de las
  // sucursales — pero la sucursal Las Condes tiene cargado en Supabase
  // Storage ese mismo logo (visualmente idéntico, URL distinta), así que
  // salía duplicado en la rotación. Ahora el panel rota SOLO entre
  // sucursales reales (branch.logoUrl); el logo genérico queda únicamente
  // como respaldo si ninguna sucursal tiene logo propio todavía.
  const heroSlides = useMemo(() => {
    const branchSlides = branches.filter((b) => b.logoUrl).map((b) => ({ logo: b.logoUrl, alt: b.name }));
    if (branchSlides.length > 0) return branchSlides;
    return [{ logo: '/logo-optica-comunal.png', alt: 'Óptica Comunal' }];
  }, [branches]);

  const [slideIndex, setSlideIndex] = useState(0);
  const [slideVisible, setSlideVisible] = useState(true);

  // Rotación automática cada 4s con fade de 0.35s (ver CSS .is-fading) — se
  // reinicia si cambia la cantidad de slides (ej. config recién cargó).
  // Si solo hay un slide (sin sucursales con logo todavía), no rota nada.
  useEffect(() => {
    if (heroSlides.length <= 1) return undefined;
    let fadeTimeout;
    const interval = setInterval(() => {
      setSlideVisible(false);
      fadeTimeout = setTimeout(() => {
        setSlideIndex((i) => (i + 1) % heroSlides.length);
        setSlideVisible(true);
      }, 350);
    }, 4000);
    return () => {
      clearInterval(interval);
      clearTimeout(fadeTimeout);
    };
  }, [heroSlides.length]);

  const activeSlide = heroSlides[slideIndex % heroSlides.length] ?? heroSlides[0];

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-header__brand">
          <strong>Agenda Comunal</strong>
          <span className="landing-header__sub">by GlobalVision</span>
        </div>
        <img
          src="/agendadock-logo.png"
          alt="AgendaDock"
          className="landing-header__powered-logo"
        />
        {/* Botón "Acceso profesionales" oculto (1-oct-2026, pedido de
            Nicolás, ver DECISIONS.md): ya no es necesario exponerlo en la
            página pública — el acceso al software ahora es solo por link
            directo (/login), no un botón visible para cualquier visitante. */}
      </header>

      <main>
        {/* Hero en dos columnas (30-sep-2026, DECISIONS.md #126) — reemplaza
            el bloque centrado anterior, tomando la estructura de
            opticacomunallascondes.cl (referencia visual de Nicolás):
            etiqueta + título + texto + dos CTA a la izquierda, panel de
            datos reales (no una foto de stock) a la derecha. */}
        <section className="landing-hero">
          <div className="landing-hero__text">
            <span className="landing-hero__tag">Agendamiento en línea</span>
            <h1>
              Agenda tu hora en las <span>ópticas comunales</span>.
            </h1>
            <p>
              Agenda Comunal, en colaboración con AgendaDock®, es un sistema de agendamiento y control de
              atenciones utilizado por las distintas ópticas comunales para coordinar horas de oftalmología y
              otorrinolaringología, donde se encuentren disponibles. Contamos con auto-agendamiento en línea y
              gestión de agenda para el equipo profesional de cada óptica, entre otras funcionalidades.
            </p>
            <div className="landing-hero__cta-row">
              <Link to="/agendamiento-online" className="button-primary landing-hero__cta">
                Agendar una hora
              </Link>
              <a href="#sucursales" className="landing-hero__cta-ghost">
                Conoce las ópticas ↓
              </a>
            </div>
          </div>
          {/* Simplificado (30-sep-2026, pedido de Nicolás): panel con logo +
              caption, 3/4 del ancho del panel. Desde 1-oct-2026 (ver
              DECISIONS.md) el logo rota automáticamente entre Óptica Comunal
              y las sucursales activas con logo propio (heroSlides, arriba)
              — fade suave vía la clase is-fading. El caption queda fijo
              (pedido de Nicolás, ronda 2): no rota, siempre dice
              "Agendamiento 100% online". */}
          <div className="landing-hero__panel" aria-hidden="true">
            <img
              src={activeSlide.logo}
              alt={activeSlide.alt}
              className={`landing-hero__panel-bigLogo${slideVisible ? '' : ' is-fading'}`}
            />
            <span className="landing-hero__panel-caption">Agendamiento 100% online</span>
          </div>
        </section>

        <section className="landing-branches" id="sucursales">
          <h2>Ópticas comunales con las que colaboramos</h2>
          <p className="landing-branches__intro">
            En esta página encontrarás la información centralizada de las ópticas comunales que colaboran
            con nosotros:
          </p>

          {error && <p className="auth-error">{error}</p>}

          {!config && !error && <p className="landing-branches__loading">Cargando…</p>}

          {config && branches.length === 0 && !error && (
            <p className="landing-branches__loading">
              Todavía no hay sucursales cargadas — vuelve a revisar pronto.
            </p>
          )}

          {branches.length > 0 && (
            <div className="landing-branches__grid">
              {branches.map((b) => (
                <BranchCard key={b.id} branch={b} />
              ))}
            </div>
          )}
        </section>

        {/* Alianzas (30-sep-2026, DECISIONS.md #127, pedido de Nicolás) —
            logos estáticos en /public, a diferencia de los logos de
            sucursal (branch.logoUrl) que se administran desde Configuración.
            Estos no cambian tan seguido y no tienen un módulo propio
            todavía, así que van directo como archivo del repo. */}
        <section className="landing-partners">
          <h2>Alianzas y colaboradores</h2>
          <div className="landing-partners__grid">
            {/* Cada logo enlaza a su sitio (30-sep-2026, pedido de Nicolás).
                Corporación y Óptica Comunal abren en pestaña nueva (sitios
                externos). Aurentec queda SIN link (30-sep-2026, DECISIONS.md
                #128): va a tener su propio dominio y sitio aparte
                (aurentec-site/) — hasta que lo compre y despliegue, este
                logo no apunta a ningún lado. src/pages/AurentecInfo.jsx
                (la versión que vivía dentro de esta app en
                /audifonos-aurentec) queda en el código sin enlazar desde
                ningún lado, por si se necesita de referencia. */}
            <a
              className="landing-partners__logo"
              href="https://corplascondes.cl/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/logo-las-condes.png" alt="Municipalidad de Las Condes — Corporación de Educación y Salud" />
            </a>
            <a
              className="landing-partners__logo"
              href="https://opticacomunallascondes.cl/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/logo-optica-comunal.png" alt="Óptica Comunal" />
            </a>
            {/* Vitacura (1-oct-2026, pedido de Nicolás, ver DECISIONS.md):
                segunda óptica comunal + su municipalidad, mismo patrón que
                Las Condes (logo + link externo en pestaña nueva). */}
            <a
              className="landing-partners__logo"
              href="https://opticacomunalvitacura.cl/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/logo-optica-comunal-vitacura.png" alt="Óptica Comunal Vitacura" />
            </a>
            <a
              className="landing-partners__logo"
              href="https://vitacura.cl/salud/inicio/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/logo-vitacura-municipalidad.png" alt="Municipalidad de Vitacura" />
            </a>
            <div className="landing-partners__logo landing-partners__logo--static">
              <img src="/logo-aurentec.png" alt="Aurentec" />
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <img src="/agendadock-logo.png" alt="AgendaDock" className="landing-footer__logo" />
        <p className="landing-footer__brand">Agendadock®</p>
        <p className="landing-footer__rights">Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
